import { motion } from "framer-motion";

export default function Home() {
  const words = [
    "IT Consultancy",
    "Networking",
    "Marketing",
    "Branding",
    "Advertising",
    "Web Development",
    "App Development",
    "Cloud Solutions",
    "Cybersecurity",
    "UI/UX Design",
    "Digital Signage Solution"
  ];

  return (
    <div className="h-screen w-full mt-10 bg-white flex flex-col md:flex-row items-center justify-between px-6 md:px-20 relative overflow-hidden">

      {/* ================== Animated Shapes ================== */}
      {/* Orange Square */}
      <motion.div
        className="absolute top-10 right-10 md:top-16 md:right-32 w-12 h-12 md:w-20 md:h-20 bg-orange-500 rotate-12"
        animate={{ y: [0, 40, -40, 0], rotate: [0, 30, -30, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Gray Triangle */}
      <motion.div
        className="absolute bottom-16 left-10 md:bottom-28 md:left-28 w-0 h-0 border-l-[25px] border-r-[25px] border-b-[40px] md:border-l-[40px] md:border-r-[40px] md:border-b-[70px] border-l-transparent border-r-transparent border-b-gray-600"
        animate={{ x: [0, 50, -50, 0], rotate: [0, 15, -15, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Orange Hexagon */}
      <motion.div
        className="absolute top-1/3 left-1/2 hidden md:block"
        animate={{ y: [0, -30, 30, 0], x: [0, 20, -20, 0], rotate: [0, 360] }}
        transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
      >
        <div className="w-20 h-20 bg-orange-400 clip-path-hexagon" />
      </motion.div>

      {/* Light Gray Diamond */}
      <motion.div
        className="absolute top-28 left-1/4 w-10 h-10 md:w-16 md:h-16 bg-gray-400 rotate-45"
        animate={{ y: [0, -50, 50, 0], rotate: [0, 90, 180, 360] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Light Gray Pentagon */}
      <motion.div
        className="absolute top-1/4 right-6 md:right-10"
        animate={{ x: [0, -40, 40, 0], y: [0, 30, -30, 0], rotate: [0, 20, -20, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-10 h-10 md:w-16 md:h-16 bg-gray-500 clip-path-pentagon" />
      </motion.div>

      {/* Orange Ring */}
      <motion.div
        className="absolute bottom-[30%] right-[10%] w-12 h-12 md:w-20 md:h-20 border-4 border-orange-400 rounded-full"
        animate={{ scale: [1, 1.3, 1], rotate: [0, 180, 360] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* ================== Left Text ================== */}
      <div className="max-w-xl z-10 text-center md:text-left mt-16 md:mt-0">
       <motion.h1
        className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-relaxed md:leading-snug"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1 }}
      >
        Lightning fast <br />
        <span className="text-orange-500">Reliable IT Solutions</span>
      </motion.h1>


        <motion.p
          className="mt-4 md:mt-6 text-base md:text-lg text-gray-600"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.2, delay: 0.3 }}
        >
          Consultancy • Networking • Branding & Marketing <br />
          Changing the way you grow with{" "}
          <span className="text-orange-400 font-semibold">technology <span className="text-gray-600"> & </span> creativity</span>.
        </motion.p>

        <a href="/services">
        <motion.button
          className="mt-6 md:mt-8 px-6 md:px-8 py-2 md:py-3 bg-orange-500 text-white font-semibold rounded-full shadow-md hover:bg-gray-500 transition duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Get Started
        </motion.button>
        </a>
      </div>

      {/* ================== Word Tree (Right Side on Large - One Continuous Lightning Effect) ================== */}
      <div className="hidden lg:flex flex-col gap-6 lg:mr-28">
        {/* Arrange words in rows of 3 but animate in a single sequence */}
        {Array.from({ length: Math.ceil(words.length / 3) }, (_, rowIndex) => (
          <div key={rowIndex} className="flex gap-8">
            {words.slice(rowIndex * 3, rowIndex * 3 + 3).map((word, i) => {
              const index = rowIndex * 3 + i; // absolute index across all words
              return (
                <motion.span
                  key={index}
                  className="text-xl md:text-2xl font-bold text-black"
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: index * 0.6, // chain lightning effect across all words
                    ease: "easeInOut",
                  }}
                >
                  {word}
                </motion.span>
              );
            })}
          </div>
        ))}
      </div>


      {/* ================== Word Cloud (Center for Small/Medium) ================== */}
     <div className="flex lg:hidden w-full mt-4 relative flex-wrap justify-center">
        {words.map((word, i) => (
          <motion.span
            key={i}
            className="m-2 text-sm md:text-lg font-semibold text-gray-700"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 1,
              delay: i * 0.2,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            {word}
          </motion.span>
        ))}
      </div>

      <div className="absolute inset-0 top-[75%] hidden md:flex items-center justify-center">
  <p className="text-4xl font-semibold text-center text-gray-700">
    Let’s build it together — your <span className="text-orange-500">vision</span>, our <span className="text-orange-500">expertise</span>.
  </p>
</div>



    </div>
  );
}
